/**
 * API client for Azure Functions endpoints.
 *
 * Routes match Hank's Azure Functions (see api/src/functions/):
 *   GET  /api/starter          — Full sync (starter + feedings + schedules)
 *   GET  /api/starter/status   — Quick health check
 *   GET  /api/starter/feedings — Recent feeding history
 *   GET  /api/starter/schedule — Active baking schedules
 *   POST /api/starter/auth     — PIN verification → JWT
 *   POST /api/starter/feeding  — Log a feeding
 *   POST /api/starter/settings — Update app/email settings
 *   POST /api/starter/wizard   — Update wizard state
 *   PUT  /api/starter/schedule — Create/update baking schedule
 *   PUT  /api/starter/status   — Update starter metadata
 */

const API_BASE = '/api';

interface ApiOptions {
  method?: string;
  body?: unknown;
  token?: string;
}

async function apiRequest<T>(endpoint: string, options: ApiOptions = {}): Promise<T> {
  const { method = 'GET', body, token } = options;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    // Use custom header — Azure SWA replaces the standard Authorization header
    headers['X-Session-Token'] = token;
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined,
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new ApiError(response.status, (errorData as { message?: string }).message ?? 'Request failed');
  }

  return response.json() as Promise<T>;
}

export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
  }
}

// Auth API — POST /api/starter/auth
export const authApi = {
  verifyPin: (pin: string) =>
    apiRequest<{ token: string; expiresIn: string }>(
      '/starter/auth',
      { method: 'POST', body: { pin } },
    ),
};

// Starter API — GET & POST under /api/starter/*
export const starterApi = {
  getFullSync: (token: string) =>
    apiRequest('/starter', { token }),

  getStatus: (token: string) =>
    apiRequest('/starter/status', { token }),

  getFeedings: (token: string, limit = 20) =>
    apiRequest(`/starter/feedings?limit=${limit}`, { token }),

  recordFeeding: (date: string, notes: string | undefined, token: string) =>
    apiRequest('/starter/feeding', {
      method: 'POST',
      body: { date, notes, source: 'pwa' },
      token,
    }),

  createSchedule: (bakeDate: string, notes: string | undefined, token: string) =>
    apiRequest('/starter/schedule', {
      method: 'PUT',
      body: { action: 'create', bakeDate, notes },
      token,
    }),
};

// Settings API — POST /api/starter/settings
export const settingsApi = {
  updateSettings: (
    settings: {
      starterName?: string;
      feedingReminderEnabled?: boolean;
      feedingReminderThresholdDays?: number;
      timezone?: string;
      emailNotifications?: boolean;
      emailAddresses?: Array<{ name: string; email: string }>;
    },
    token: string,
  ) =>
    apiRequest('/starter/settings', {
      method: 'POST',
      body: settings,
      token,
    }),
};
